import { useState } from "react";
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Tooltip,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation, useNavigate } from "react-router-dom";

interface DrawerMenuItemProps {
  item: {
    key: string;
    path?: string;
    text: string;
    icon: React.ElementType;
    children: Array<{ key: string; path: string; text: string; icon: React.ElementType }>;
  };
  drawerOpen: boolean;
}

const selectedColor = '#E50019';

function DrawerMenuItem({ item, drawerOpen }: DrawerMenuItemProps) {
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hasSubmenu = item.children.length > 0;

  const isSelected = location.pathname.startsWith(item.path || "");
  const isSubItemSelected = (path: string) => location.pathname.startsWith(path);

  const handleItemClick = () => {
    if (hasSubmenu) {
      setSubmenuOpen(!submenuOpen);
      if (!submenuOpen && item.children[0]?.path) {
        navigate(item.children[0].path);
      }
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <Box sx={{
      '&:last-child': { borderBottom: 'none' },
      borderRadius: '8px',
      overflow: 'hidden',
      mx: 1,
      my: 0.5
    }}>
      <ListItem
        disablePadding
        sx={{
          backgroundColor: isSelected ? selectedColor : 'inherit',
          borderRadius: '8px',
          // Transition for background color change
          transition: 'background-color 0.1s ease-in-out',
        }}
      >
        <Tooltip
          title={!drawerOpen ? item.text : ''}
          placement="right"
          arrow
          disableHoverListener={drawerOpen}
        >
          <ListItemButton
            onClick={handleItemClick}
            sx={{
              py: '12px',
              // The collapsed rail leaves ~54px of room, so 16px of side padding
              // plus the 40px icon slot overflows and clips the icon.
              px: drawerOpen ? '16px' : '8px',
              justifyContent: drawerOpen ? 'flex-start' : 'center',
              borderRadius: '8px',
              overflow: 'hidden',
              '&:hover': {
                backgroundColor: isSelected ? selectedColor : 'rgba(0, 0, 0, 0.04)'
              },
              // Transition for padding/flex changes related to drawerOpen
              transition: 'padding 0.1s ease-in-out',
            }}
          >
            <ListItemIcon sx={{
              minWidth: drawerOpen ? '40px' : 'auto',
              flexShrink: 0,
              justifyContent: 'center',
              color: isSelected ? '#fff' : 'rgba(0, 0, 0, 0.87)',
              transition: 'color 0.1s ease-in-out', // Transition for icon color
            }}>
              <item.icon color={isSelected ? "#fff" : undefined} />
            </ListItemIcon>

            {/* Use Box for conditional rendering with potential animation via CSS transition/transform */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1, // Allows text to take available space
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    opacity: drawerOpen ? 1 : 0, // Animate opacity
                    width: drawerOpen ? 'auto' : 0, // Animate width
                    transition: 'opacity 0.1s ease-in-out, width 0.1s ease-in-out',
                    // Optional: If you want a slight horizontal slide, you could use transform
                    // transform: drawerOpen ? 'translateX(0)' : 'translateX(-20px)',
                    // transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                }}
            >
              {drawerOpen && ( // Only render text and arrows if drawer is open to avoid layout shifts
                <>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '1rem',
                        fontWeight: isSelected ? 600 : 400,
                        color: isSelected ? '#fff' : 'rgba(0, 0, 0, 0.87)',
                        marginLeft: '8px',
                        transition: 'color 0.1s ease-in-out', // Transition for text color
                      }
                    }}
                  />

                  {isSelected && !hasSubmenu && (
                    <KeyboardArrowRightIcon sx={{
                      color: '#fff',
                      ml: 1,
                      fontSize: '1.25rem',
                      transition: 'color 0.1s ease-in-out', // Transition for arrow color
                    }} />
                  )}

                  {hasSubmenu && (
                    <Tooltip title={submenuOpen ? "Collapse" : "Expand"} placement="right" arrow>
                      <IconButton
                        edge="end"
                        size="small"
                        sx={{
                          color: isSelected ? '#fff' : 'rgba(0, 0, 0, 0.54)',
                          '&:hover': {
                            backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)'
                          },
                          transition: 'color 0.1s ease-in-out, background-color 0.3s ease-in-out', // Transition for IconButton
                        }}
                      >
                        {submenuOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>


          </ListItemButton>
        </Tooltip>
      </ListItem>

      {drawerOpen && hasSubmenu && submenuOpen && (
        <Box sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
          overflow: 'hidden',
          // Optional: Add a transition for height if you want a subtle expand/collapse effect for the submenu
          // maxHeight: submenuOpen ? '500px' : '0', // Adjust max-height as needed
          // transition: 'max-height 0.3s ease-in-out',
        }}>
          {item.children.map((subItem) => (
            <ListItem
              key={subItem.key}
              disablePadding
              sx={{
                pl: '56px',
                backgroundColor: isSubItemSelected(subItem.path) ? selectedColor : 'inherit',
                borderRadius: '8px',
                transition: 'background-color 0.1s ease-in-out', // Transition for submenu item background
              }}
            >
              <Tooltip title={subItem.text} placement="right" arrow>
                <ListItemButton
                  onClick={() => navigate(subItem.path)}
                  sx={{
                    py: '8px',
                    px: '16px',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: isSubItemSelected(subItem.path) ? selectedColor : 'rgba(0, 0, 0, 0.04)'
                    },
                    transition: 'padding 0.1s ease-in-out', // Transition for submenu button padding
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: '40px',
                    color: isSubItemSelected(subItem.path) ? '#fff' : 'rgba(0, 0, 0, 0.87)',
                    transition: 'color 0.1s ease-in-out', // Transition for submenu icon color
                  }}>
                    <subItem.icon fontSize="small" sx={{ color: isSubItemSelected(subItem.path) ? '#fff' : 'rgba(0, 0, 0, 0.87)' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={subItem.text}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: '0.875rem',
                        fontWeight: isSubItemSelected(subItem.path) ? 500 : 400,
                        color: isSubItemSelected(subItem.path) ? '#fff' : 'rgba(0, 0, 0, 0.87)',
                        marginLeft: '8px',
                        transition: 'color 0.1s ease-in-out', // Transition for submenu text color
                      }
                    }}
                  />
                  {isSubItemSelected(subItem.path) && (
                    <KeyboardArrowRightIcon sx={{
                      color: '#fff',
                      ml: 1,
                      fontSize: '1.25rem',
                      transition: 'color 0.1s ease-in-out', // Transition for submenu arrow color
                    }} />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default DrawerMenuItem;